import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  ShieldCheck,
  CopySimple,
  ArrowSquareOut,
  CheckCircle,
  CircleNotch,
  Warning,
  Receipt,
  XCircle,
} from "@phosphor-icons/react";

const COPY_FIELDS = [
  { key: "brand", label: "Brand" },
  { key: "model", label: "Model" },
  { key: "serial", label: "Serial Number" },
  { key: "purchase_date", label: "Purchase Date" },
  { key: "purchase_price", label: "Purchase Price" },
  { key: "location", label: "Location" },
];

export default function WarrantyConciergeDrawer({
  open,
  onOpenChange,
  assetId,
  assetName,
  onRegistered,
}) {
  const { canWrite } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [duration, setDuration] = useState("");
  const [expirationOverride, setExpirationOverride] = useState("");
  const [notes, setNotes] = useState("");
  const [step, setStep] = useState("companion"); // 'companion' | 'confirm'

  const hydrate = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const r = await api.get(`/warranty/${assetId}`);
      setData(r.data);
      setDuration(r.data?.suggested_duration_months ? String(r.data.suggested_duration_months) : "12");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load warranty workspace.");
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (open && assetId) {
      setStep("companion");
      setExpirationOverride("");
      setNotes("");
      hydrate();
    }
  }, [open, assetId, hydrate]);

  const copy = async (label, text) => {
    if (!text) {
      toast.error(`${label} is blank — nothing to copy.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Copy failed.");
    }
  };

  const launchRegistration = () => {
    if (!data?.registration_url) {
      toast.error("No registration URL available — please search manually.");
      return;
    }
    window.open(data.registration_url, "_blank", "noopener,noreferrer");
  };

  const markRegistered = async () => {
    if (!canWrite) return toast.error("Read-only role.");
    setBusy(true);
    try {
      const body = { notes: notes || undefined };
      if (expirationOverride) {
        body.expiration_date = expirationOverride;
      } else if (duration) {
        const m = parseInt(duration, 10);
        if (!Number.isNaN(m) && m > 0) body.duration_months = m;
      }
      const r = await api.post(`/warranty/${assetId}/mark-registered`, body);
      toast.success("Warranty logged. Maintenance reminder scheduled.");
      setData((d) => ({
        ...(d || {}),
        warranty_status: "registered",
        warranty_expiration_date: r.data.warranty_expiration_date,
        warranty_notes: r.data.warranty_notes,
      }));
      onRegistered?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not log the warranty.");
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    if (!canWrite) return toast.error("Read-only role.");
    if (!window.confirm("Skip warranty registration for this asset? You can re-enable it later.")) return;
    setBusy(true);
    try {
      await api.post(`/warranty/${assetId}/skip`, {});
      toast.success("Skipped — Leo will stop nudging.");
      onRegistered?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Skip failed.");
    } finally {
      setBusy(false);
    }
  };

  const defaultExpiration = useMemo(() => {
    if (!data?.purchase_date || !duration) return null;
    const m = parseInt(duration, 10);
    if (Number.isNaN(m) || m <= 0) return null;
    const d = new Date(data.purchase_date);
    if (Number.isNaN(d.getTime())) return null;
    d.setMonth(d.getMonth() + m);
    return d.toISOString().slice(0, 10);
  }, [data?.purchase_date, duration]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl flex flex-col p-0 bg-[#F9F8F6]"
        data-testid="warranty-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#E0DDD6] bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#2E4D77] flex items-center justify-center border border-[#1E3554] shrink-0">
              <ShieldCheck size={18} weight="regular" color="#F9F8F6" />
            </div>
            <div className="min-w-0">
              <SheetTitle className="font-serif-display text-xl text-[#0A192F] leading-tight">
                Warranty Concierge
              </SheetTitle>
              <SheetDescription className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                {assetName ? `Leo · ${assetName}` : "Leo · register before you forget"}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#5C5F58]">
              <CircleNotch size={14} className="animate-spin" /> Loading workspace…
            </div>
          )}

          {!loading && data && data.eligible === false && (
            <NotEligible reason={data.reason} />
          )}

          {!loading && data && data.eligible && step === "companion" && (
            <CompanionStep
              data={data}
              onCopy={copy}
              onLaunch={launchRegistration}
              onProceed={() => setStep("confirm")}
              onSkip={skip}
              canWrite={canWrite}
              busy={busy}
            />
          )}

          {!loading && data && data.eligible && step === "confirm" && (
            <ConfirmStep
              data={data}
              duration={duration}
              setDuration={setDuration}
              expirationOverride={expirationOverride}
              setExpirationOverride={setExpirationOverride}
              defaultExpiration={defaultExpiration}
              notes={notes}
              setNotes={setNotes}
              onBack={() => setStep("companion")}
              onConfirm={markRegistered}
              busy={busy}
              canWrite={canWrite}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function NotEligible({ reason }) {
  const msg =
    reason === "no_brand"
      ? "This asset doesn't have a brand on record. Add a brand to enable warranty tracking."
      : reason === "excluded_class"
        ? "Warranty Concierge is hidden for this asset class — it isn't useful for fine art or generic items."
        : "Warranty Concierge isn't available for this asset.";
  return (
    <div className="bg-white border border-[#E0DDD6] px-4 py-6 text-sm text-[#5C5F58]" data-testid="warranty-not-eligible">
      {msg}
    </div>
  );
}

function CompanionStep({ data, onCopy, onLaunch, onProceed, onSkip, canWrite, busy }) {
  const isRegistered = data.warranty_status === "registered";
  const isLeoSourced = data.url_source === "leo";
  const noUrl = !data.registration_url;

  return (
    <div className="space-y-5" data-testid="warranty-companion-step">
      {isRegistered && (
        <div className="bg-[#1A3622] text-[#F9F8F6] border border-[#0F2818] px-4 py-3 text-sm flex items-start gap-2">
          <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0" />
          <div>
            <strong>Warranty already registered.</strong>
            {data.warranty_expiration_date && (
              <> Expires {data.warranty_expiration_date}.</>
            )}
          </div>
        </div>
      )}

      {(isLeoSourced || noUrl) && (
        <div className="bg-[#F4E9D2] border border-[#C5A059] px-4 py-3 text-xs text-[#5C3D08] flex items-start gap-2" data-testid="warranty-leo-guardrail">
          <Warning size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>Verify the URL before submitting any data.</strong> Leo suggested this link
            from general knowledge — it&apos;s not in our curated registry.
            {data.url_note && <> <em>{data.url_note}</em></>}
          </div>
        </div>
      )}

      {/* Launch button */}
      {data.registration_url ? (
        <button
          onClick={onLaunch}
          data-testid="warranty-launch-btn"
          className="w-full bg-[#2E4D77] text-[#F9F8F6] py-3 text-sm font-medium border border-[#1E3554] hover:bg-[#1E3554] inline-flex items-center justify-center gap-2"
        >
          <ArrowSquareOut size={14} weight="bold" /> Open Manufacturer Registration ↗
        </button>
      ) : (
        <div className="bg-white border border-[#E0DDD6] px-4 py-3 text-sm text-[#5C5F58]">
          No registration URL available for <strong>{data.copy_payload.brand}</strong>. Search the
          manufacturer&apos;s site directly, then return here to log the warranty.
        </div>
      )}

      {/* Receipt download */}
      {data.receipt_path && (
        <a
          href={`${API}/files/${data.receipt_path}`}
          target="_blank"
          rel="noreferrer"
          data-testid="warranty-receipt-link"
          className="bg-white border border-[#E0DDD6] px-4 py-3 flex items-center gap-3 text-sm text-[#0A192F] hover:bg-[#F9F8F6] transition-colors"
        >
          <Receipt size={16} className="text-[#5C5F58] shrink-0" />
          <span className="flex-1">Open receipt from Vault</span>
          <ArrowSquareOut size={12} className="text-[#5C5F58]" />
        </a>
      )}

      {/* Copy buttons */}
      <div>
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
          One-click clipboard
        </div>
        <ul className="bg-white border border-[#E0DDD6] divide-y divide-[#E0DDD6]" data-testid="warranty-copy-list">
          {COPY_FIELDS.map((f) => {
            const value = data.copy_payload[f.key] || "";
            return (
              <li
                key={f.key}
                className="px-4 py-3 flex items-center gap-3"
                data-testid={`warranty-copy-row-${f.key}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
                    {f.label}
                  </div>
                  <div className={`text-sm mt-0.5 truncate ${value ? "text-[#0A192F]" : "text-[#5C5F58] italic"}`}>
                    {value || "—"}
                  </div>
                </div>
                <button
                  onClick={() => onCopy(f.label, value)}
                  disabled={!value}
                  data-testid={`warranty-copy-${f.key}`}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border border-[#E0DDD6] bg-white hover:border-[#1A3622] text-[#0A192F] disabled:opacity-40"
                  title={value ? `Copy ${f.label}` : `${f.label} is blank`}
                >
                  <CopySimple size={12} /> Copy
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Action footer */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={onProceed}
          disabled={busy || !canWrite || isRegistered}
          data-testid="warranty-proceed-btn"
          className="flex-1 bg-[#1A3622] text-[#F9F8F6] py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          <CheckCircle size={14} weight="bold" /> Yes, I registered it
        </button>
        <button
          onClick={onSkip}
          disabled={busy || !canWrite}
          data-testid="warranty-skip-btn"
          className="px-4 py-2.5 text-sm border border-[#E0DDD6] bg-white hover:border-[#6B1718] text-[#6B1718] disabled:opacity-50 inline-flex items-center gap-2"
        >
          <XCircle size={14} /> Skip
        </button>
      </div>
    </div>
  );
}

function ConfirmStep({
  data, duration, setDuration, expirationOverride, setExpirationOverride,
  defaultExpiration, notes, setNotes, onBack, onConfirm, busy, canWrite,
}) {
  return (
    <div className="space-y-5" data-testid="warranty-confirm-step">
      <button
        onClick={onBack}
        data-testid="warranty-back-btn"
        className="text-[10px] font-mono-ledger uppercase tracking-[0.25em] text-[#5C5F58] hover:text-[#1A3622]"
      >
        ← Back to companion
      </button>

      <div>
        <div className="font-serif-display text-xl text-[#0A192F]">Lock the details.</div>
        <div className="text-xs text-[#5C5F58] mt-0.5">
          Leo will schedule a reminder 60 days before expiry.
        </div>
      </div>

      <div className="bg-white border border-[#E0DDD6] px-4 py-3 space-y-3">
        <div>
          <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
            Warranty Duration (months)
          </label>
          <input
            type="number"
            min="1"
            max="1200"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            data-testid="warranty-duration-input"
            className="w-full border border-[#E0DDD6] bg-white px-3 py-2 text-sm focus:border-[#1A3622] focus:outline-none"
          />
          {data.duration_rationale && (
            <div className="text-[11px] text-[#5C5F58] italic mt-1" data-testid="warranty-duration-rationale">
              Leo: {data.duration_rationale}
            </div>
          )}
        </div>
        <div>
          <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
            Expiration Date {defaultExpiration && <span className="opacity-60 normal-case">(default: {defaultExpiration})</span>}
          </label>
          <input
            type="date"
            value={expirationOverride}
            onChange={(e) => setExpirationOverride(e.target.value)}
            placeholder={defaultExpiration || ""}
            data-testid="warranty-expiration-input"
            className="w-full border border-[#E0DDD6] bg-white px-3 py-2 text-sm focus:border-[#1A3622] focus:outline-none"
          />
          <div className="text-[11px] text-[#5C5F58] mt-1">
            Leave blank to use the duration above.
          </div>
        </div>
        <div>
          <label className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder='e.g. "Parts only in Year 2"'
            data-testid="warranty-notes-input"
            className="w-full border border-[#E0DDD6] bg-white px-3 py-2 text-sm focus:border-[#1A3622] focus:outline-none resize-none"
          />
        </div>
      </div>

      <button
        onClick={onConfirm}
        disabled={busy || !canWrite}
        data-testid="warranty-confirm-btn"
        className="w-full bg-[#1A3622] text-[#F9F8F6] py-3 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy ? (
          <>
            <CircleNotch size={14} className="animate-spin" /> Logging…
          </>
        ) : (
          <>
            <CheckCircle size={14} weight="bold" /> Log it · schedule T-60 reminder
          </>
        )}
      </button>
    </div>
  );
}
